import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CABINES, DEFAULT_CABINE, applyCabine, getCabine } from '../config/cabines';
import { Anchor, ArrowLeft, Check, Lock, Flame, Compass, Star } from 'lucide-react';

/** Aperçu d'une cabine : mêmes composants que l'app, mais avec les jetons de la cabine. */
const CabinePreview = ({ cabine }) => {
  const t = cabine.tokens;
  const hsl = (token, fallback) => `hsl(${t[token] || fallback})`;
  const isInk = cabine.id === 'encre-nuit';

  return (
    <div
      className="rounded-2xl overflow-hidden border"
      style={{
        background: hsl('--background'),
        borderColor: hsl('--border'),
        backgroundImage: cabine.texture,
        backgroundSize: cabine.textureSize,
      }}
      data-testid={`cabine-preview-${cabine.id}`}
    >
      <div
        className="px-4 py-3 flex items-center justify-between gap-3"
        style={{
          background: isInk ? hsl('--primary') : 'transparent',
          borderBottom: `1px solid ${hsl('--border')}`,
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
            style={{
              background: isInk ? hsl('--accent') : hsl('--card'),
              border: `1.5px solid ${hsl('--accent')}`,
              color: isInk ? hsl('--primary') : hsl('--accent'),
            }}
          >
            <Anchor className="w-4 h-4" />
          </span>
          <span
            className="font-semibold truncate text-[0.95rem]"
            style={{
              fontFamily: cabine.heading,
              color: isInk ? hsl('--primary-foreground') : hsl('--foreground'),
            }}
          >
            {cabine.name}
          </span>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full shrink-0"
          style={{
            background: isInk ? 'hsla(40,47%,96%,0.14)' : hsl('--card'),
            color: isInk ? hsl('--primary-foreground') : hsl('--accent'),
            border: `1px solid ${isInk ? 'hsla(40,47%,96%,0.2)' : hsl('--border')}`,
          }}
        >
          {cabine.minLevel === 0 ? 'Offerte' : `Niv. ${cabine.minLevel}`}
        </span>
      </div>

      <div
        style={{ height: 2, background: `linear-gradient(90deg, ${hsl('--accent')}, transparent 70%)` }}
      />

      <div className="p-4 space-y-3">
        <div
          className="p-4"
          style={{
            background: hsl('--card'),
            border: `1px solid ${hsl('--border')}`,
            borderRadius: t['--radius'],
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-sm font-bold"
              style={{
                background: hsl('--background'),
                border: `2px solid ${hsl('--accent')}`,
                color: hsl('--accent'),
              }}
            >
              AO
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate" style={{ fontFamily: cabine.heading, color: hsl('--foreground') }}>
                Ton nom
              </p>
              <p className="text-[10px] uppercase tracking-[0.18em] truncate" style={{ color: hsl('--accent') }}>
                Capitaine au long cours
              </p>
            </div>
            <span
              className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-full shrink-0"
              style={{ background: hsl('--background'), border: `1px solid ${hsl('--border')}`, color: hsl('--primary') }}
            >
              <Flame className="w-3 h-3" />
              12 j
            </span>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] mb-1" style={{ color: hsl('--muted-foreground') }}>
              <span>Niveau 9</span>
              <span>8 420 XP</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: hsl('--border') }}>
              <div
                className="h-full"
                style={{ width: '72%', background: `linear-gradient(90deg, ${hsl('--primary')}, ${hsl('--accent')})` }}
              />
            </div>
          </div>
        </div>

        <div
          className="p-3 flex items-center gap-3"
          style={{ background: hsl('--card'), border: `1px solid ${hsl('--border')}`, borderRadius: t['--radius'] }}
        >
          <span
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: hsl('--background'), color: hsl('--accent') }}
          >
            <Compass className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate" style={{ color: hsl('--foreground') }}>
              Chapitre 9 — Marchander au port
            </p>
            <p className="text-[11px]" style={{ color: hsl('--muted-foreground') }}>
              Savoir négocier · +60 XP
            </p>
          </div>
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
            style={{ background: hsl('--accent'), color: hsl('--card') }}
          >
            <Check className="w-4 h-4" />
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            className="flex-1 h-10 text-sm font-medium"
            style={{ background: hsl('--primary'), color: hsl('--primary-foreground'), borderRadius: t['--radius'] }}
          >
            Reprendre ma formation
          </button>
          <button
            className="h-10 px-3 flex items-center justify-center"
            style={{ color: hsl('--accent'), border: `1px solid ${hsl('--accent')}`, borderRadius: t['--radius'] }}
          >
            <Star className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const CabinesPage = () => {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ current: user?.cabine || DEFAULT_CABINE, level: user?.level || 1, cabines: [] });
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get('/cabines')
      .then((res) => setState(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlockedIds = new Set(
    (state.cabines || []).filter((c) => c.unlocked).map((c) => c.id)
  );
  const minLevelOf = (id) =>
    (state.cabines || []).find((c) => c.id === id)?.min_level ?? getCabine(id).minLevel;

  const choose = async (id) => {
    setSaving(id);
    try {
      await api.put('/users/me/cabine', { cabine: id });
      applyCabine(id);
      setState((s) => ({ ...s, current: id }));
      if (refreshUser) refreshUser();
      toast.success(`Cabine « ${getCabine(id).name} » installée`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de changer de cabine');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="cabines-page">
      <header className="border-b border-border bg-card/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/profile')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="font-heading font-bold text-base sm:text-xl truncate">Ma Cabine</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              3 cabines offertes, les autres se méritent · Niveau {state.level}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CABINES.map((cabine) => {
            const unlocked = unlockedIds.size ? unlockedIds.has(cabine.id) : cabine.minLevel === 0;
            const isCurrent = state.current === cabine.id;
            return (
              <div key={cabine.id} className="space-y-3" data-testid={`cabine-card-${cabine.id}`}>
                <div className={unlocked ? '' : 'opacity-60 grayscale-[35%]'}>
                  <CabinePreview cabine={cabine} />
                </div>
                <div className="px-1 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-heading font-semibold text-sm">{cabine.name}</p>
                    {cabine.minLevel === 0 ? (
                      <Badge variant="secondary" className="text-[10px]">Offerte</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Niveau {minLevelOf(cabine.id)}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{cabine.tagline}</p>
                  {isCurrent ? (
                    <Button size="sm" disabled className="w-full" data-testid={`cabine-current-${cabine.id}`}>
                      <Check className="w-4 h-4 mr-1" />
                      Cabine actuelle
                    </Button>
                  ) : unlocked ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      disabled={saving === cabine.id}
                      onClick={() => choose(cabine.id)}
                      data-testid={`cabine-choose-${cabine.id}`}
                    >
                      S&apos;installer dans cette cabine
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" disabled className="w-full" data-testid={`cabine-locked-${cabine.id}`}>
                      <Lock className="w-4 h-4 mr-1" />
                      Débloquée au niveau {minLevelOf(cabine.id)}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default CabinesPage;
